import { useContext, useState, useEffect } from "react";
import { LoginFormContext } from "context/loginFormCtx";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import { updateTenantDetails } from "lib/Api/Tenants";

const ADDRESS_TYPE = {
  BILLING: 1,
  COMPANY: 2,
};

const areAddressesEqual = (addr1, addr2) => {
  if (!addr1 || !addr2) return false;
  if (!addr1.address_line_1 || !addr2.address_line_1) return false;
  if (!addr1.zip_code || !addr2.zip_code) return false;

  return (
    (addr1.address_line_1 || "") === (addr2.address_line_1 || "") &&
    (addr1.address_line_2 || "") === (addr2.address_line_2 || "") &&
    (addr1.city || "") === (addr2.city || "") &&
    (addr1.country || "") === (addr2.country || "") &&
    (addr1.zip_code || "") === (addr2.zip_code || "")
  );
};

const EditTenantDetails = ({ tenantDetails, getTenantDetails }) => {
  const { id } = useParams();
  const { setNotification } = useContext(LoginFormContext);
  const [sameAddress, setSameAddress] = useState(false);

  const validationSchema = Yup.object().shape({
    company_name: Yup.string().trim().required("Company name is required"),
    billingAddress: Yup.array().of(
      Yup.object().shape({
        address_line_1: Yup.string()
          .trim()
          .required("Address line 1 is required")
          .max(255, "Address line 1 must be less than 255 characters"),
        address_line_2: Yup.string()
          .trim()
          .optional()
          .nullable()
          .max(255, "Address line 1 must be less than 255 characters"),
        country: Yup.string()
          .trim()
          .optional()
          .nullable()
          .max(255, "Address line 1 must be less than 255 characters"),
        city: Yup.string()
          .trim()
          .optional()
          .nullable()
          .max(255, "Address line 1 must be less than 255 characters"),
        zip_code: Yup.string()
          .trim()
          .required("Zip code is required")
          .max(10, "Zip code must be less than 10 characters"),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      ...(tenantDetails || {}),
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);

        const temp = {
          company_name: values?.company_name || "",
          billingAddress: values?.billingAddress || [],
        };

        const response = await updateTenantDetails(id, temp);

        if (response && response.status === 200) {
          setSubmitting(false);
          resetForm();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
          getTenantDetails();
        } else {
          setSubmitting(false);
          setNotification({
            color: "error",
            icon: "cancel",
            title: "Error",
            content: response.data.message,
            open: true,
          });
        }
      } catch (error) {
        setSubmitting(false);
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: error.message || "Something went wrong!",
          open: true,
        });
      }
    },
  });

  useEffect(() => {
    if (formik.values.billingAddress) {
      setSameAddress(
        areAddressesEqual(formik.values.billingAddress?.[0], formik.values.billingAddress?.[1])
      );
    }
  }, [formik?.values]);

  const handleSameAddress = () => {
    if (sameAddress) {
      setSameAddress(false);
      return;
    }
    const billingAddress = formik.values.billingAddress?.find(
      (addr) => addr.address_type === ADDRESS_TYPE.BILLING
    );
    const companyAddressIndex = formik.values.billingAddress?.findIndex(
      (addr) => addr.address_type === ADDRESS_TYPE.COMPANY
    );

    formik.setFieldValue(`billingAddress.${companyAddressIndex}`, {
      ...(formik.values.billingAddress?.[companyAddressIndex] || {}),
      address_line_1: billingAddress?.address_line_1 || "",
      address_line_2: billingAddress?.address_line_2 || "",
      city: billingAddress?.city || "",
      country: billingAddress?.country || "",
      zip_code: billingAddress?.zip_code || "",
    });
    setSameAddress(true);
  };

  return (
    <form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item sm={6}>
          <TextField
            label="Company Name"
            name="company_name"
            value={formik.values.company_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            required
            error={Boolean(formik.touched.company_name && formik.errors.company_name)}
            helperText={formik.touched.company_name && formik.errors.company_name}
          />
        </Grid>
        <Grid item sm={6} />

        {formik?.values?.billingAddress?.map((addr, index) => (
          <Grid
            item
            sm={12}
            lg={6}
            key={index}
            sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                pb: addr.address_type === ADDRESS_TYPE.BILLING ? 1.7 : 0,
              }}
            >
              {addr.address_type === ADDRESS_TYPE.BILLING ? "Billing Address" : "Company Address"}

              {addr.address_type === ADDRESS_TYPE.COMPANY && (
                <FormControlLabel
                  control={<Checkbox checked={sameAddress} onChange={handleSameAddress} />}
                  label="Same as Billing Address"
                />
              )}
            </Typography>
            <TextField
              label={`Address Line 1`}
              name={`billingAddress.${index}.address_line_1`}
              value={addr.address_line_1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              required
              error={Boolean(
                formik.touched?.billingAddress?.[index]?.address_line_1 &&
                  formik.errors?.billingAddress?.[index]?.address_line_1
              )}
              helperText={
                formik.touched?.billingAddress?.[index]?.address_line_1 &&
                formik.errors?.billingAddress?.[index]?.address_line_1
              }
              disabled={addr.address_type === ADDRESS_TYPE.COMPANY && sameAddress}
            />
            <TextField
              label={`Address Line 2`}
              name={`billingAddress.${index}.address_line_2`}
              value={addr.address_line_2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={Boolean(
                formik.touched?.billingAddress?.[index]?.address_line_2 &&
                  formik.errors?.billingAddress?.[index]?.address_line_2
              )}
              helperText={
                formik.touched?.billingAddress?.[index]?.address_line_2 &&
                formik.errors?.billingAddress?.[index]?.address_line_2
              }
              disabled={addr.address_type === ADDRESS_TYPE.COMPANY && sameAddress}
            />
            <TextField
               label={`City`}
              name={`billingAddress.${index}.city`}
              value={addr.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={Boolean(
                formik.touched?.billingAddress?.[index]?.city &&
                  formik.errors?.billingAddress?.[index]?.city
              )}
              helperText={
                formik.touched?.billingAddress?.[index]?.city &&
                formik.errors?.billingAddress?.[index]?.city
              }
              disabled={addr.address_type === ADDRESS_TYPE.COMPANY && sameAddress}
            />
          
            <TextField
              label={`Country`}
              name={`billingAddress.${index}.country`}
              value={addr.country}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={Boolean(
                formik.touched?.billingAddress?.[index]?.country &&
                  formik.errors?.billingAddress?.[index]?.country
              )}
              helperText={
                formik.touched?.billingAddress?.[index]?.country &&
                formik.errors?.billingAddress?.[index]?.country
              }
              disabled={addr.address_type === ADDRESS_TYPE.COMPANY && sameAddress}
            />
            <TextField
              label={`Zip Code`}
              name={`billingAddress.${index}.zip_code`}
              value={addr.zip_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              required
              error={Boolean(
                formik.touched?.billingAddress?.[index]?.zip_code &&
                  formik.errors?.billingAddress?.[index]?.zip_code
              )}
              helperText={
                formik.touched?.billingAddress?.[index]?.zip_code &&
                formik.errors?.billingAddress?.[index]?.zip_code
              }
              disabled={addr.address_type === ADDRESS_TYPE.COMPANY && sameAddress}
            />
          </Grid>
        ))}
      </Grid>

      <Divider />
      <MDBox sx={{ mt: 2 }} display="flex" justifyContent="flex-end" alignItems="center">
        <Button disabled={formik.isSubmitting} type="submit" variant="contained">
          Update Tenant Details
        </Button>
      </MDBox>
    </form>
  );
};

export default EditTenantDetails;
